import * as React from 'react';
import noop from 'lodash/noop';

// Can't import a browser-only into a "universal" package, but
// in this case we should be safe since the browser apis in useCopyToClipboard
// is inside a function call
// eslint-disable-next-line webflow/no-dangerous-imports
import {useCopyToClipboardWithTooltip} from '@packages/systems/ui/hooks/useCopyToClipboard';

import {DuplicateFillIcon, CheckDefaultIcon} from '@packages/systems/spring';

import {ControlledTooltipTrigger} from '@packages/systems/design-system/TooltipTrigger';
import {Text} from '@packages/systems/design-system/Text';
import {LogicLabel} from './LogicInput';
import {styled, emotionStyled, tokens} from '@packages/utilities/styled';
import {oneLineText} from '@packages/systems/design-system/constants/Styles';

const Wrapper = emotionStyled('div')({
  marginTop: tokens.space[1],
});

const TextCopyPill = emotionStyled('div')({
  display: 'grid',
  gridAutoFlow: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'default',
  backgroundColor: tokens.colors.background1,
  padding: '$2 $4',
  borderRadius: tokens.controls.borderRadius,
  border: `1px solid ${tokens.colors.border1}`,
});

const GreenCheckIcon = styled(CheckDefaultIcon)(({theme}) => ({
  color: theme.colors.successAccent,
  fontSize: 0.5,
})) as typeof CheckDefaultIcon;

const StyledText = styled(Text)(
  ({theme}) => ({
    ...oneLineText,
    marginRight: theme.gutters.x4,
  }),
  {
    dim: ({theme: {colors}}) => ({
      color: colors.logicFlows.foregroundDimmer,
    }),
  }
);

// Icons to accompany tooltip states
const icons = {
  hovered: (
    <DuplicateFillIcon
      size={12}
      className="bem-SlugPreview_Icon"
      style={{display: 'inline-block'}}
      contrast="minimum"
    />
  ),
  success: (
    <GreenCheckIcon
      size={12}
      className="bem-SlugPreview_Icon"
      style={{display: 'inline-block'}}
    />
  ),
} as const;

/**
 * This component renders a copiable text.
 */
export const LogicCopyToClipboard = React.memo<{
  /** The actual text to copy to clipboard. */
  textToCopy: string;
  /** Optional label to render above the copy pill */
  label?: string;
  /** Dim font style */
  dim?: boolean;
  /** ID for automation tests */
  ['data-automation-id']?: string;
  /** Custom content to render on hover. Defaults to "Click to copy" */
  hoverContent?: React.ReactNode;
  /** Custom error content to render when copy fails. Defaults to "Copy failed" */
  errorContent?: React.ReactNode;
  /** Custom success content to render on copy success. Defaults to "Copied!" */
  successContent?: React.ReactNode;
}>(
  ({
    hoverContent = 'Click to copy',
    errorContent = 'Copy failed',
    successContent = 'Copied!',
    textToCopy,
    label = '',
    dim,
    'data-automation-id': dataAutomationId,
  }) => {
    const {
      handleClick,
      handleMouseEnter,
      handleMouseLeave,
      tooltipProps,
      state,
    } = useCopyToClipboardWithTooltip({
      errorContent,
      hoverContent,
      onComplete: noop,
      onError: noop,
      successContent,
      value: textToCopy,
      mouseEnterDelay: 0,
      resetDelay: 1250,
    });

    const icon = (() => {
      if (state === 'hovered') return icons.hovered;
      if (state === 'success') return icons.success;
      return null;
    })();

    return (
      <Wrapper>
        <LogicLabel label={label} />
        <ControlledTooltipTrigger alternate {...tooltipProps}>
          <TextCopyPill
            data-automation-id={dataAutomationId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <StyledText dim={dim}>{textToCopy}</StyledText>
            {icon}
          </TextCopyPill>
        </ControlledTooltipTrigger>
      </Wrapper>
    );
  }
);
